// GaleriasManager.js — galerías de eventos realizados por venue, FRA-300.
//
// Entidad curada por el venue, completamente desacoplada de `eventos` y
// `proveedores`: nada operativo (clientes, proveedores contratados) pasa por
// aquí, así que no hay problema de privacidad al publicarla. Solo las
// galerías con `publicada: true` llegan al homesite (filtro en Flask).
// `fecha` es {mes, anio} sin día a propósito — no identifica un evento.

import React, { useState, useEffect, useMemo } from "react";
import {
  collection,
  doc,
  getDocs,
  onSnapshot,
  writeBatch,
  Timestamp,
} from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "./AuthProvider";
import { Input } from "./input";
import { Switch } from "./switch";
import { Checkbox } from "./checkbox";
import { Button } from "./button";
import {
  Dialog,
  DialogTitle,
  DialogBody,
  DialogActions,
  DialogLabel,
} from "./dialog";
import BilingualDescriptionField from "./BilingualDescriptionField";
import { ImagesField } from "./ImagesField";
import InstagramPostsField, { IG_POST_RE, MAX_IG_POSTS } from "./InstagramPostsField";
import { showAlert } from "../alertService";
import { logBitacora, BITACORA_ACTIONS } from "../utils/bitacora";
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  ChevronUpIcon,
  ChevronDownIcon,
} from "@heroicons/react/20/solid";

const ACCENT = "#1b4d2e";

const MESES = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
];

const sortGalerias = (list) =>
  [...list].sort(
    (a, b) =>
      (a.orden ?? 0) - (b.orden ?? 0) ||
      (a.titulo || "").localeCompare(b.titulo || "")
  );

const EMPTY_FORM = {
  titulo: "",
  descripcion: "",
  descripcionEn: "",
  tipoEvento: "",
  numeroInvitados: "",
  fechaMes: "",
  fechaAnio: "",
  espaciosUsados: [],
  fotos: [],
  instagramPosts: [],
  publicada: false,
};

const GaleriasManager = ({ venueId, espacios = [], readOnly = false, onVenuePatch }) => {
  const { userInfo, user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [eventTypes, setEventTypes] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [deleting, setDeleting] = useState(null);

  const list = useMemo(() => sortGalerias(items), [items]);
  const espaciosActivos = useMemo(
    () => espacios.filter((e) => e && e.activo !== false),
    [espacios]
  );

  // Lectura en vivo, no una sola al montar: la ficha del venue se deja
  // abierta mientras se editan galerías desde otro lado (o se siembran
  // datos), y con getDocs la lista se quedaba vacía hasta recargar. El error
  // además se muestra en la propia sección, no solo como toast, para que se
  // vea la causa (típicamente permisos) sin abrir la consola.
  useEffect(() => {
    if (!venueId) return undefined;
    setLoading(true);
    const unsub = onSnapshot(
      collection(db, "venues", venueId, "galerias"),
      (snap) => {
        setItems(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoadError(null);
        setLoading(false);
      },
      (err) => {
        console.error("Error cargando galerías:", err);
        setLoadError(err?.code || err?.message || "error desconocido");
        setLoading(false);
      }
    );
    return unsub;
  }, [venueId]);

  useEffect(() => {
    getDocs(collection(db, "eventTypes"))
      .then((snap) => {
        const docs = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        docs.sort((a, b) => (a.orden || 0) - (b.orden || 0));
        setEventTypes(docs);
      })
      .catch((e) => console.error("Error loading eventTypes:", e));
  }, []);

  const tipoLabel = (id) =>
    eventTypes.find((et) => et.id === id)?.label?.ES || id;

  const nombreEspacio = (id) =>
    espacios.find((e) => e.id === id)?.nombre || id;


  // Contador denormalizado de galerías PUBLICADAS en el doc del venue: el
  // sitemap lo usa para saber si /eventos existe sin leer esta subcolección
  // en el endpoint de listado. Mismo patrón que el rollup de espacios, y
  // como aquel se avisa a VenuePage con onVenuePatch para que un "Guardar
  // cambios" posterior no lo pise con la copia vieja del formulario. El
  // contador viaja en el MISMO batch que la galería: si se escribieran por
  // separado y la segunda escritura fallara, el sitemap anunciaría /eventos
  // para un venue cuya página responde 404 (o al revés).
  const contadorOps = (batch, lista) => {
    const galeriasPublicadasCount = lista.filter((g) => g.publicada === true).length;
    batch.update(doc(db, "venues", venueId), { galeriasPublicadasCount });
    return galeriasPublicadasCount;
  };

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setDialogOpen(true);
  };

  const openEdit = (g) => {
    setEditingId(g.id);
    setForm({
      titulo: g.titulo || "",
      descripcion: g.descripcion || "",
      descripcionEn: g.descripcionEn || "",
      tipoEvento: g.tipoEvento || "",
      numeroInvitados: g.numeroInvitados ?? "",
      instagramPosts: g.instagramPosts || [],
      fechaMes: g.fecha?.mes ?? "",
      fechaAnio: g.fecha?.anio ?? "",
      espaciosUsados: g.espaciosUsados || [],
      fotos: g.fotos || [],
      publicada: g.publicada === true,
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    const titulo = form.titulo.trim();
    if (!titulo) {
      showAlert("El título de la galería no puede estar vacío.", "error");
      return;
    }
    if (form.publicada && form.fotos.length === 0) {
      showAlert("Una galería publicada necesita al menos una foto.", "error");
      return;
    }
    setSaving(true);
    try {
      const editing = editingId ? items.find((g) => g.id === editingId) : null;
      const ref = editing
        ? doc(db, "venues", venueId, "galerias", editing.id)
        : doc(collection(db, "venues", venueId, "galerias"));
      const mes = form.fechaMes === "" ? null : Number(form.fechaMes);
      const anio = form.fechaAnio === "" ? null : Number(form.fechaAnio);
      const data = {
        titulo,
        descripcion: form.descripcion,
        descripcionEn: form.descripcionEn,
        tipoEvento: form.tipoEvento || null,
        numeroInvitados:
          form.numeroInvitados === "" ? null : Number(form.numeroInvitados),
        fecha: mes && anio ? { mes, anio } : null,
        espaciosUsados: form.espaciosUsados,
        fotos: form.fotos,
        // Solo links válidos y sin repetir, máximo 3: se filtra aquí para
        // no guardar basura pegada del código de incrustación, y el Set
        // evita que el mismo post se embeba tres veces.
        instagramPosts: [
          ...new Set((form.instagramPosts || []).filter((u) => IG_POST_RE.test(u))),
        ].slice(0, MAX_IG_POSTS),
        publicada: form.publicada,
        orden: editing ? editing.orden ?? 0 : list.length,
        createdAt: editing?.createdAt || Timestamp.now(),
      };
      const siguiente = editing
        ? items.map((g) => (g.id === editing.id ? { id: g.id, ...data } : g))
        : [...items, { id: ref.id, ...data }];
      const batch = writeBatch(db);
      batch.set(ref, data);
      const galeriasPublicadasCount = contadorOps(batch, siguiente);
      await batch.commit();
      setItems(siguiente);
      if (onVenuePatch) onVenuePatch({ fields: { galeriasPublicadasCount } });
      logBitacora(
        venueId,
        {
          action: editing
            ? BITACORA_ACTIONS.GALERIA_EDIT
            : BITACORA_ACTIONS.GALERIA_CREATE,
          entity: "galeria",
          entityId: ref.id,
          entityName: titulo,
          details: { publicada: form.publicada },
        },
        userInfo,
        user
      );
      setDialogOpen(false);
    } catch (err) {
      console.error("Error guardando galería:", err);
      showAlert("Error al guardar la galería.", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    const g = deleting;
    if (!g) return;
    setSaving(true);
    try {
      const siguiente = items.filter((x) => x.id !== g.id);
      const batch = writeBatch(db);
      batch.delete(doc(db, "venues", venueId, "galerias", g.id));
      const galeriasPublicadasCount = contadorOps(batch, siguiente);
      await batch.commit();
      setItems(siguiente);
      if (onVenuePatch) onVenuePatch({ fields: { galeriasPublicadasCount } });
      logBitacora(
        venueId,
        {
          action: BITACORA_ACTIONS.GALERIA_DELETE,
          entity: "galeria",
          entityId: g.id,
          entityName: g.titulo || "",
          details: {},
        },
        userInfo,
        user
      );
      setDeleting(null);
    } catch (err) {
      console.error("Error eliminando galería:", err);
      showAlert("Error al eliminar la galería.", "error");
    } finally {
      setSaving(false);
    }
  };

  const move = async (g, dir) => {
    const idx = list.findIndex((x) => x.id === g.id);
    const target = idx + dir;
    if (target < 0 || target >= list.length) return;
    const next = [...list];
    [next[idx], next[target]] = [next[target], next[idx]];
    const normalized = next.map((x, i) => ({ ...x, orden: i }));
    setSaving(true);
    try {
      const batch = writeBatch(db);
      normalized.forEach(({ id, ...data }) => {
        batch.update(doc(db, "venues", venueId, "galerias", id), {
          orden: data.orden,
        });
      });
      await batch.commit();
      setItems(normalized);
    } catch (err) {
      console.error("Error reordenando galerías:", err);
      showAlert("Error al reordenar.", "error");
    } finally {
      setSaving(false);
    }
  };

  const metaLinea = (g) => {
    const parts = [];
    if (g.tipoEvento) parts.push(tipoLabel(g.tipoEvento));
    if (g.numeroInvitados) parts.push(`${g.numeroInvitados} invitados`);
    if (g.fecha?.mes && g.fecha?.anio)
      parts.push(`${MESES[g.fecha.mes - 1]} ${g.fecha.anio}`);
    if ((g.fotos || []).length > 0) parts.push(`${g.fotos.length} fotos`);
    return parts.join(" · ");
  };

  if (loading) {
    return <div className="text-sm text-zinc-500">Cargando galerías…</div>;
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-zinc-500">
        Arma galerías de eventos que ya hiciste — fotos, tipo de evento y en
        qué espacios fue. Las publicadas aparecen en tu página pública; nada de
        tus eventos operativos (clientes, proveedores) se publica.
      </p>

      {loadError && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          No se pudieron cargar las galerías ({loadError}). Si el problema
          sigue, recarga la página.
        </div>
      )}

      {list.length > 0 && (
        <ul className="divide-y divide-zinc-100 rounded-lg border border-zinc-200">
          {list.map((g, idx) => (
            <li key={g.id} className="flex items-center gap-3 px-3 py-2.5">
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium text-zinc-900">{g.titulo}</span>
                  {g.publicada ? (
                    <span className="inline-flex items-center rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-700 ring-1 ring-emerald-200">
                      Publicada
                    </span>
                  ) : (
                    <span className="inline-flex items-center rounded-full bg-zinc-100 px-2 py-0.5 text-[11px] font-medium text-zinc-500">
                      Borrador
                    </span>
                  )}
                </div>
                <div className="mt-0.5 flex flex-wrap gap-x-3 text-xs text-zinc-500">
                  {metaLinea(g) && <span>{metaLinea(g)}</span>}
                  {(g.espaciosUsados || []).length > 0 && (
                    <span>
                      {g.espaciosUsados.map(nombreEspacio).join(", ")}
                    </span>
                  )}
                </div>
              </div>
              {!readOnly && (
                <div className="flex shrink-0 items-center gap-1">
                  <button
                    type="button"
                    onClick={() => move(g, -1)}
                    disabled={idx === 0 || saving}
                    className="rounded p-1 text-zinc-400 hover:bg-zinc-100 hover:text-zinc-700 disabled:opacity-30"
                    title="Subir"
                  >
                    <ChevronUpIcon className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => move(g, 1)}
                    disabled={idx === list.length - 1 || saving}
                    className="rounded p-1 text-zinc-400 hover:bg-zinc-100 hover:text-zinc-700 disabled:opacity-30"
                    title="Bajar"
                  >
                    <ChevronDownIcon className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => openEdit(g)}
                    disabled={saving}
                    className="rounded p-1 text-zinc-400 hover:bg-zinc-100 hover:text-zinc-700"
                    title="Editar"
                  >
                    <PencilIcon className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setDeleting(g)}
                    disabled={saving}
                    className="rounded p-1 text-zinc-400 hover:bg-red-50 hover:text-red-600"
                    title="Eliminar"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      {!readOnly && (
        <button
          type="button"
          onClick={openCreate}
          disabled={saving}
          className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-semibold text-white shadow-sm transition hover:opacity-90 disabled:opacity-50"
          style={{ backgroundColor: ACCENT }}
        >
          <PlusIcon className="h-4 w-4" />
          Agregar galería
        </button>
      )}

      {/* Dialog crear/editar */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} size="3xl">
        <DialogTitle>{editingId ? "Editar galería" : "Nueva galería"}</DialogTitle>
        <DialogBody className="space-y-5">
          <div className="grid gap-4 sm:grid-cols-[1fr_auto]">
            <div>
              <DialogLabel>Título</DialogLabel>
              <div className="mt-1">
                <Input
                  value={form.titulo}
                  onChange={(e) => setForm((f) => ({ ...f, titulo: e.target.value }))}
                  placeholder="Boda en el jardín, primavera 2026"
                />
              </div>
            </div>
            <label className="flex items-center gap-2 self-start pt-6">
              <Switch
                color="accent"
                checked={form.publicada}
                onChange={(checked) => setForm((f) => ({ ...f, publicada: checked }))}
              />
              <span className="text-sm text-zinc-700">
                {form.publicada ? "Publicada" : "Borrador"}
              </span>
            </label>
          </div>

          <BilingualDescriptionField
            valueEs={form.descripcion}
            valueEn={form.descripcionEn}
            onChangeEs={(v) => setForm((f) => ({ ...f, descripcion: v }))}
            onChangeEn={(v) => setForm((f) => ({ ...f, descripcionEn: v }))}
            rows={3}
          />

          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <div className="col-span-2">
              <DialogLabel>Tipo de evento</DialogLabel>
              <select
                value={form.tipoEvento}
                onChange={(e) => setForm((f) => ({ ...f, tipoEvento: e.target.value }))}
                className="mt-1 w-full rounded-lg border border-zinc-950/10 bg-white px-3 py-1.5 text-sm shadow-sm focus:outline-none"
              >
                <option value="">Sin tipo</option>
                {eventTypes.map((et) => (
                  <option key={et.id} value={et.id}>
                    {et.label?.ES || et.id}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <DialogLabel># de invitados</DialogLabel>
              <div className="mt-1">
                <Input
                  type="number"
                  min="0"
                  value={form.numeroInvitados}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, numeroInvitados: e.target.value }))
                  }
                />
              </div>
            </div>
            <div>
              <DialogLabel>Fecha (mes/año)</DialogLabel>
              <div className="mt-1 flex gap-1.5">
                <select
                  value={form.fechaMes}
                  onChange={(e) => setForm((f) => ({ ...f, fechaMes: e.target.value }))}
                  className="w-full rounded-lg border border-zinc-950/10 bg-white px-2 py-1.5 text-sm shadow-sm focus:outline-none"
                >
                  <option value="">Mes</option>
                  {MESES.map((m, i) => (
                    <option key={m} value={i + 1}>
                      {m}
                    </option>
                  ))}
                </select>
                <select
                  value={form.fechaAnio}
                  onChange={(e) => setForm((f) => ({ ...f, fechaAnio: e.target.value }))}
                  className="w-full rounded-lg border border-zinc-950/10 bg-white px-2 py-1.5 text-sm shadow-sm focus:outline-none"
                >
                  <option value="">Año</option>
                  {Array.from({ length: 12 }, (_, i) => new Date().getFullYear() - i).map(
                    (y) => (
                      <option key={y} value={y}>
                        {y}
                      </option>
                    )
                  )}
                </select>
              </div>
            </div>
          </div>

          {espaciosActivos.length > 0 && (
            <div>
              <DialogLabel>Espacios donde ocurrió</DialogLabel>
              <div className="mt-2 space-y-1.5 rounded-lg border border-zinc-200 p-3">
                {espaciosActivos.map((esp) => {
                  const checked = form.espaciosUsados.includes(esp.id);
                  return (
                    <label key={esp.id} className="flex items-center gap-2">
                      <Checkbox
                        color="accent"
                        checked={checked}
                        onChange={(c) =>
                          setForm((f) => ({
                            ...f,
                            espaciosUsados: c
                              ? [...f.espaciosUsados, esp.id]
                              : f.espaciosUsados.filter((x) => x !== esp.id),
                          }))
                        }
                      />
                      <span className="text-sm text-zinc-700">{esp.nombre}</span>
                    </label>
                  );
                })}
              </div>
            </div>
          )}

          <ImagesField
            label="Fotos del evento"
            images={form.fotos}
            onReorderImages={(fotos) => setForm((f) => ({ ...f, fotos }))}
            venueId={venueId}
          />

          <InstagramPostsField
            value={form.instagramPosts}
            onChange={(arr) => setForm((f) => ({ ...f, instagramPosts: arr }))}
            label="Posts de Instagram de este evento"
            help="Opcional. Pega hasta 3 links de posts o reels de este evento — sirve el link directo o el código de incrustación completo que te da Instagram. En tu página pública aparece un botón para verlos dentro de esta galería."
          />
        </DialogBody>
        <DialogActions>
          <Button plain type="button" onClick={() => setDialogOpen(false)}>
            Cancelar
          </Button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="rounded-lg px-4 py-1.5 text-sm font-semibold text-white shadow-sm transition hover:opacity-90 disabled:opacity-50"
            style={{ backgroundColor: ACCENT }}
          >
            {saving ? "Guardando…" : editingId ? "Guardar cambios" : "Crear galería"}
          </button>
        </DialogActions>
      </Dialog>

      {/* Confirmar borrado */}
      <Dialog open={!!deleting} onClose={() => setDeleting(null)} size="lg">
        <DialogTitle>Eliminar galería</DialogTitle>
        <DialogBody className="text-sm text-zinc-700">
          <p>
            ¿Eliminar <span className="font-semibold">{deleting?.titulo}</span>?
            {deleting?.publicada &&
              " Está publicada: desaparecerá de tu página pública."}
          </p>
        </DialogBody>
        <DialogActions>
          <Button plain type="button" onClick={() => setDeleting(null)}>
            Cancelar
          </Button>
          <Button color="red" type="button" onClick={handleDelete} disabled={saving}>
            {saving ? "Eliminando…" : "Eliminar"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default GaleriasManager;
